/**
 * escposPrinter.ts
 *
 * Impresión vía Web Bluetooth API con codificación ESC/POS.
 * Funciona en navegadores Chrome/Edge que soporten Web Bluetooth.
 * Compatible con impresoras térmicas POS externas (Bluetooth BLE).
 */
import { parseBackendDate, formatHoraCR, formatFechaCR } from '../date';

// ---------------------------------------------------------------------------
// ESC/POS encoding
// ---------------------------------------------------------------------------

export function toEscPosBytes(text: string, ancho: 58 | 80 = 80): Uint8Array {
  const encoder = new TextEncoder();
  const bytes: number[] = [];

  // Init printer (no codepage command — SUNMI doesn't support WPC1252)
  bytes.push(0x1b, 0x40);

  const lineLength = ancho === 58 ? 32 : 48;
  const lines = text.split('\n').map((line) => line.trimEnd());

  for (const line of lines) {
    if (!line) {
      bytes.push(0x0a);
      continue;
    }
    if (line.length <= lineLength) {
      const lineBytes = Array.from(encoder.encode(line));
      bytes.push(...lineBytes, 0x0a);
      continue;
    }

    for (let i = 0; i < line.length; i += lineLength) {
      const part = line.slice(i, i + lineLength);
      bytes.push(...Array.from(encoder.encode(part)), 0x0a);
    }
  }

  // Feed extra para separar ticket (sin comando de corte — SUNMI no tiene cortador)
  bytes.push(0x0a, 0x0a, 0x0a, 0x0a);

  return new Uint8Array(bytes);
}

// ---------------------------------------------------------------------------
// Bluetooth types & constants
// ---------------------------------------------------------------------------

const LS_PRINTER_KEY = 'tiempos_bluetooth_printer';

interface PrinterInfo {
  id: string;
  name: string;
  serviceUuid?: string;
  characteristicUuid?: string;
}

interface CaracteristicaEscritura {
  uuid?: string;
  properties: { write?: boolean; writeWithoutResponse?: boolean };
  writeValue: (value: BufferSource) => Promise<void>;
}

interface ServicioGatt {
  uuid?: string;
  getCharacteristic: (uuid: string) => Promise<CaracteristicaEscritura>;
  getCharacteristics: () => Promise<CaracteristicaEscritura[]>;
}

interface ServidorGatt {
  getPrimaryService: (uuid: string) => Promise<ServicioGatt>;
  getPrimaryServices: () => Promise<ServicioGatt[]>;
}

export const CANDIDATE_SERVICE_UUIDS = [
  '0000ffe0-0000-1000-8000-00805f9b34fb',
  '000018f0-0000-1000-8000-00805f9b34fb',
  '49535343-fe7d-4ae5-8fa9-9fafd205e455',
];

export const CANDIDATE_CHARACTERISTICS = [
  '0000ffe1-0000-1000-8000-00805f9b34fb',
  '49535343-1e4d-4bd9-ba61-23c647249616',
  '00002af1-0000-1000-8000-00805f9b34fb',
];

// ---------------------------------------------------------------------------
// Bluetooth helpers
// ---------------------------------------------------------------------------

async function resolveWritableCharacteristic(
  server: ServidorGatt,
): Promise<{ serviceUuid?: string; characteristic: CaracteristicaEscritura; characteristicUuid?: string }> {
  for (const serviceUuid of CANDIDATE_SERVICE_UUIDS) {
    try {
      const service = await server.getPrimaryService(serviceUuid);
      for (const characteristicUuid of CANDIDATE_CHARACTERISTICS) {
        try {
          const characteristic = await service.getCharacteristic(characteristicUuid);
          if (characteristic.properties.write || characteristic.properties.writeWithoutResponse) {
            return { serviceUuid, characteristic, characteristicUuid };
          }
        } catch {
          // try next characteristic
        }
      }
      const chars = await service.getCharacteristics();
      const writable = chars.find(
        (c: CaracteristicaEscritura) => c.properties.write || c.properties.writeWithoutResponse,
      );
      if (writable) {
        return { serviceUuid, characteristic: writable, characteristicUuid: writable.uuid };
      }
    } catch {
      // try next service
    }
  }

  const services = await server.getPrimaryServices();
  for (const service of services) {
    const chars = await service.getCharacteristics();
    const writable = chars.find(
      (c: CaracteristicaEscritura) => c.properties.write || c.properties.writeWithoutResponse,
    );
    if (writable) {
      return { serviceUuid: service.uuid, characteristic: writable, characteristicUuid: writable.uuid };
    }
  }

  throw new Error('No se encontró una característica Bluetooth de escritura para la impresora.');
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getSavedBluetoothPrinter(): PrinterInfo | null {
  try {
    const raw = localStorage.getItem(LS_PRINTER_KEY);
    return raw ? (JSON.parse(raw) as PrinterInfo) : null;
  } catch {
    return null;
  }
}

export async function connectBluetoothPrinter(): Promise<PrinterInfo> {
  const bluetooth = (navigator as any).bluetooth;
  if (!bluetooth) {
    throw new Error('Este navegador no soporta Web Bluetooth.');
  }

  const device = await bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: CANDIDATE_SERVICE_UUIDS,
  });

  if (!device.gatt) {
    throw new Error('El dispositivo no expone GATT para impresión BLE.');
  }

  const server = await device.gatt.connect();
  const { serviceUuid, characteristicUuid } = await resolveWritableCharacteristic(server);

  const info: PrinterInfo = {
    id: device.id,
    name: device.name || 'Impresora POS',
    serviceUuid,
    characteristicUuid,
  };
  localStorage.setItem(LS_PRINTER_KEY, JSON.stringify(info));

  return info;
}

export async function printTicketBluetooth(
  ticketText: string,
  options?: { ancho?: 58 | 80 },
): Promise<void> {
  const bluetooth = (navigator as any).bluetooth;
  if (!bluetooth) {
    throw new Error('Web Bluetooth no está disponible en este dispositivo.');
  }

  const device = await bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: CANDIDATE_SERVICE_UUIDS,
  });

  if (!device.gatt) {
    throw new Error('No se pudo abrir conexión GATT con la impresora.');
  }

  const server = await device.gatt.connect();
  const { characteristic } = await resolveWritableCharacteristic(server);
  const bytes = toEscPosBytes(ticketText, options?.ancho || 80);

  const chunkSize = 180;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.slice(i, i + chunkSize);
    await characteristic.writeValue(chunk);
  }

  if (device.gatt.connected) {
    device.gatt.disconnect();
  }
}

/**
 * Convierte un payload de ticket a texto plano para impresión ESC/POS o SUNMI.
 * Replica fielmente la estructura del ticket web (ticketService.ts buildJugadasBlock).
 */
export function payloadToTicketText(payload: {
  ticketCode: string;
  sorteoNombre: string;
  fecha?: string;
  total: number;
  jugadas: Array<{ num1: string; num2?: string | null; monto: number; tipoSorteo?: string }>;
  localNombre?: string;
  localDescripcion?: string;
  sorteoCierre?: string;
  vendedorNombre?: string;
  clienteNombre?: string;
  pagaVecesAplicado?: string | null;
  esReimpresion?: boolean;
  leyendaLocal?: string;
  localTelefono?: string;
  fechaOperativa?: string;
}): string {
  const lines: string[] = [];
  const hr = '================================';
  const COL = 32;
  const center = (s: string) => {
    const p = Math.max(0, Math.floor((COL - s.length) / 2));
    return ' '.repeat(p) + s;
  };

  // Encabezado
  if (payload.esReimpresion) {
    lines.push(center('** REIMPRESION **'));
  }
  const nombre = (payload.localNombre || 'TIEMPOS').toUpperCase();
  lines.push(center(nombre));
  if (payload.localDescripcion) {
    lines.push(center(payload.localDescripcion));
  }

  // Sorteo destacado
  lines.push(hr);
  lines.push(center(payload.sorteoNombre.toUpperCase()));
  lines.push(hr);

  // Datos del ticket
  lines.push('TICKET:  ' + payload.ticketCode);
  if (payload.fecha) {
    lines.push('VENTA:   ' + payload.fecha);
  }
  if (payload.sorteoCierre) {
    lines.push('CIERRE:  ' + payload.sorteoCierre);
  }
  if (payload.pagaVecesAplicado) {
    lines.push('PAGA:    ' + payload.pagaVecesAplicado);
  }
  if (payload.clienteNombre && payload.clienteNombre !== 'Cliente') {
    lines.push('CLIENTE: ' + payload.clienteNombre);
  }
  if (payload.vendedorNombre) {
    lines.push('VENDEDOR:' + payload.vendedorNombre);
  }
  if (payload.localTelefono) {
    lines.push('TEL:     ' + payload.localTelefono);
  }

  // Detalle de jugadas
  lines.push(hr);
  lines.push(center('DETALLE'));
  lines.push(hr);

  const bloques = _buildNativeBloques(payload.jugadas);
  for (const bloque of bloques) {
    if (bloque.tipo === 'titulo') {
      lines.push(bloque.texto);
    } else if (bloque.tipo === 'general' || bloque.tipo === 'monazos') {
      if (bloque.tipo === 'monazos') {
        lines.push(bloque.titulo);
      }
      for (let i = 0; i < bloque.lineas.length; i++) {
        if (i === 0) {
          lines.push(bloque.monto + ' ' + bloque.lineas[i]);
        } else {
          lines.push('  ' + bloque.lineas[i]);
        }
      }
    } else if (bloque.tipo === 'reventado-tabla') {
      // 3-column table: NUMS | DIR | REV
      lines.push('NUMS       DIR     REV');
      lines.push('---------- ------- -------');
      for (const fila of bloque.filas) {
        const nums = fila.numeros.padEnd(10).slice(0, 10);
        const dir = (fila.normal || '').padStart(7);
        const rev = (fila.reventado || '').padStart(7);
        lines.push(`${nums} ${dir} ${rev}`);
      }
    }
  }

  lines.push(hr);

  // Total
  lines.push('TOTAL: ' + _fmtMonto(payload.total));
  lines.push(hr);

  // Pie
  const leyenda = payload.leyendaLocal || 'GRACIAS POR SU PREFERENCIA';
  if (payload.fechaOperativa) {
    lines.push(center('VALIDO PARA EL DIA'));
    lines.push(center(payload.fechaOperativa));
  }
  lines.push(center(leyenda));
  lines.push('TIQUETE: ' + payload.ticketCode);
  lines.push('');

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Rich ESC/POS ticket formatter — bytes with bold, size, centering
// ---------------------------------------------------------------------------

/** ESC/POS command constants */
const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

const CMD = {
  INIT: [ESC, 0x40],                         // Initialize printer
  BOLD_ON: [ESC, 0x45, 0x01],                // Bold ON
  BOLD_OFF: [ESC, 0x45, 0x00],               // Bold OFF
  ALIGN_CENTER: [ESC, 0x61, 0x01],           // Center alignment
  ALIGN_LEFT: [ESC, 0x61, 0x00],             // Left alignment
  SIZE_NORMAL: [GS, 0x21, 0x00],             // Normal size (32 cols)
  SIZE_DOUBLE_H: [GS, 0x21, 0x01],           // Double height only (32 cols)
  SIZE_DOUBLE_W: [GS, 0x21, 0x10],           // Double width only (16 cols)
  SIZE_DOUBLE_WH: [GS, 0x21, 0x11],          // Double width + double height (16 cols)
  FONT_A: [ESC, 0x4d, 0x00],                 // Font A (default, may have slashed zeros)
  FONT_B: [ESC, 0x4d, 0x01],                 // Font B (smaller, cleaner zeros)
} as const;

// ---------------------------------------------------------------------------
// Shared helpers for native ticket formatters
// ---------------------------------------------------------------------------

type Jugada = { num1: string; num2?: string | null; monto: number; tipoSorteo?: string };

type NativeBloque =
  | { tipo: 'general'; monto: string; lineas: string[] }
  | { tipo: 'monazos'; titulo: string; monto: string; lineas: string[] }
  | { tipo: 'titulo'; texto: string }
  | { tipo: 'reventado-tabla'; filas: Array<{ numeros: string; normal: string; reventado: string }> };

const MESES_CORTO: Record<string, string> = {
  '01': 'Ene', '02': 'Feb', '03': 'Mar', '04': 'Abr',
  '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Set', '10': 'Oct', '11': 'Nov', '12': 'Dic',
};

/** Word-wrap text to fit within maxLen characters per line. */
function _wrapText(text: string, maxLen: number): string[] {
  if (text.length <= maxLen) return [text];
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (!current) {
      current = word;
    } else if (current.length + 1 + word.length <= maxLen) {
      current += ' ' + word;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [text];
}

/** ASCII-safe number formatter with dots as thousands separator (Costa Rican convention). */
function _fmtMonto(n: number): string {
  const s = Math.trunc(n).toString();
  // Insert dots every 3 digits from the right
  let out = '';
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) out += '.';
    out += s[i];
  }
  return 'C' + out;
}

/** Formatter for ESC/POS native: simple C prefix, space as thousands separator. */
function _fmtMontoNative(n: number): string {
  const s = Math.trunc(n).toString();
  let out = '';
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) out += ' ';
    out += s[i];
  }
  return out;
}

/** Replicate `numeroParaTicket` from ticketService.ts */
function _numDisplay(j: Jugada): string {
  // MEGA: extract mega number
  if (j.num2 && /^MEGA:/i.test(j.num2)) {
    return j.num2.replace(/^MEGA:/i, '');
  }
  // PARLEY: two-digit num2 → num1-num2
  if (j.num2 && /^\d{2}$/.test(j.num2)) {
    return `${j.num1}-${j.num2}`;
  }
  // FECHAS: DD-MM → DD-Mes
  if (j.tipoSorteo === 'FECHAS') {
    const match = j.num1.match(/^(\d{1,2})-(\d{2})$/);
    if (match) {
      const mes = MESES_CORTO[match[2]] || match[2];
      return `${match[1]}-${mes}`;
    }
  }
  return j.num1;
}

/**
 * Replicate `buildJugadasBlock` logic from ticketService.ts for native printing.
 * Handles MONAZOS, REVENTADO, and general jugada types identically to the web.
 */
function _buildNativeBloques(jugadas: Jugada[], fmt?: (n: number) => string): NativeBloque[] {
  if (!jugadas.length) return [];
  const fmtM = fmt || _fmtMonto;

  const monazoLabels: Record<string, string> = {
    ORDEN: 'En Orden', DESORDEN: 'Desorden', P2: 'Primeros 2', U2: 'Ultimos 2',
  };

  // --- MONAZOS ---
  const esMonazos = jugadas.every(j => !!j.num2 && Object.prototype.hasOwnProperty.call(monazoLabels, j.num2));
  if (esMonazos) {
    const ordenTipos = ['ORDEN', 'DESORDEN', 'P2', 'U2'];
    const grupos = new Map<string, Map<number, string[]>>();
    for (const j of jugadas) {
      const tipo = j.num2 as string;
      if (!grupos.has(tipo)) grupos.set(tipo, new Map());
      const porMonto = grupos.get(tipo)!;
      const lista = porMonto.get(j.monto) || [];
      lista.push(j.num1);
      porMonto.set(j.monto, lista);
    }
    const bloques: NativeBloque[] = [];
    for (const tipo of ordenTipos) {
      const porMonto = grupos.get(tipo);
      if (!porMonto) continue;
      for (const [monto, numeros] of porMonto) {
        const lineas: string[] = [];
        for (let i = 0; i < numeros.length; i += 3) {
          lineas.push(numeros.slice(i, i + 3).join('-'));
        }
        bloques.push({ tipo: 'monazos', titulo: monazoLabels[tipo], monto: fmtM(monto), lineas });
      }
    }
    return bloques;
  }

  // --- REVENTADO ---
  const esReventado = jugadas.some(j => j.num2 === 'REV1' || j.num2 === 'REV2');
  if (esReventado) {
    // Group by number, collecting directo (REV1) and reventado (REV2) amounts
    const porNumero = new Map<string, { directo: number | null; reventado: number | null }>();
    for (const j of jugadas) {
      const num = j.num1;
      if (!porNumero.has(num)) porNumero.set(num, { directo: null, reventado: null });
      const entry = porNumero.get(num)!;
      if (j.num2 === 'REV2') {
        entry.reventado = j.monto;
      } else {
        entry.directo = j.monto;
      }
    }
    // Group numbers with the same directo+reventado monto combination
    const porMontoCombo = new Map<string, string[]>();
    for (const [num, montos] of porNumero) {
      const key = `${montos.directo ?? ''}_${montos.reventado ?? ''}`;
      if (!porMontoCombo.has(key)) porMontoCombo.set(key, []);
      porMontoCombo.get(key)!.push(num);
    }
    // Build rows, 3 numbers per row (was 4 — caused overflow)
    const filas: Array<{ numeros: string; normal: string; reventado: string }> = [];
    for (const [key, numeros] of porMontoCombo) {
      const [directoStr, reventadoStr] = key.split('_');
      const directo = directoStr ? Number(directoStr) : null;
      const reventado = reventadoStr ? Number(reventadoStr) : null;
      for (let i = 0; i < numeros.length; i += 3) {
        const grupo = numeros.slice(i, i + 3);
        filas.push({
          numeros: grupo.join('-'),
          normal: directo !== null ? fmtM(directo) : '',
          reventado: reventado !== null ? fmtM(reventado) : '',
        });
      }
    }
    return [{ tipo: 'reventado-tabla', filas }];
  }

  // --- General: NORMAL/TICA, FECHAS, PARLEY, MEGA ---
  const montoMap = new Map<number, string[]>();
  for (const j of jugadas) {
    const lista = montoMap.get(j.monto) || [];
    lista.push(_numDisplay(j));
    montoMap.set(j.monto, lista);
  }
  const bloques: NativeBloque[] = [];
  for (const [monto, numeros] of montoMap) {
    const lineas: string[] = [];
    for (let i = 0; i < numeros.length; i += 3) {
      lineas.push(numeros.slice(i, i + 3).join('-'));
    }
    bloques.push({ tipo: 'general', monto: fmtM(monto), lineas });
  }
  return bloques;
}

/**
 * Genera bytes ESC/POS con formateo visual para un ticket de lotería.
 * Optimizado para impresoras SUNMI integradas (V2s, V2 Pro, T2).
 * Prioriza legibilidad física real sobre minimalismo.
 */
export function ticketPayloadToEscPosBytes(payload: {
  ticketCode: string;
  sorteoNombre: string;
  fecha?: string;
  total: number;
  jugadas: Array<{ num1: string; num2?: string | null; monto: number; tipoSorteo?: string }>;
  localNombre?: string;
  localDescripcion?: string;
  sorteoCierre?: string;
  vendedorNombre?: string;
  clienteNombre?: string;
  pagaVecesAplicado?: string | null;
  esReimpresion?: boolean;
  leyendaLocal?: string;
  localTelefono?: string;
  fechaOperativa?: string;
}): Uint8Array {
  const enc = new TextEncoder();
  const b: number[] = [];

  const push = (...cmds: (readonly number[])[]) => {
    for (const cmd of cmds) b.push(...cmd);
  };
  const txt = (s: string) => b.push(...Array.from(enc.encode(s)));
  const ln = () => b.push(LF);
  const textLn = (s: string) => { txt(s); ln(); };
  const dashes = () => textLn('--------------------------------');

  // Init + Font B (cleaner zeros without slash) — re-sent after INIT to ensure it sticks
  push(CMD.INIT);
  push(CMD.FONT_B);
  // Character set: USA (code page 0) to avoid locale glyph issues
  b.push(ESC, 0x52, 0x00);

  // --- REIMPRESION banner ---
  if (payload.esReimpresion) {
    push(CMD.ALIGN_CENTER, CMD.BOLD_ON, CMD.SIZE_DOUBLE_WH);
    textLn('** REIMPRESION **');
    push(CMD.SIZE_NORMAL, CMD.BOLD_OFF);
    push(CMD.FONT_B);
  }

  // --- ENCABEZADO: nombre del local (bold + double height, centered) ---
  push(CMD.ALIGN_CENTER, CMD.BOLD_ON, CMD.SIZE_DOUBLE_H);
  textLn((payload.localNombre || 'TIEMPOS').toUpperCase());
  push(CMD.SIZE_NORMAL, CMD.BOLD_OFF, CMD.FONT_B);

  // --- SORTEO destacado (bold + double W+H, centered) ---
  dashes();
  push(CMD.BOLD_ON, CMD.SIZE_DOUBLE_WH);
  textLn(payload.sorteoNombre.toUpperCase());
  push(CMD.SIZE_NORMAL, CMD.BOLD_OFF, CMD.FONT_B);
  dashes();

  // --- Datos meta (left-aligned, normal size + bold) ---
  push(CMD.ALIGN_LEFT, CMD.BOLD_ON);
  if (payload.fecha) {
    txt('VENTA: '); textLn(payload.fecha);
  }
  if (payload.pagaVecesAplicado) {
    txt('Paga: '); textLn(payload.pagaVecesAplicado);
  }
  if (payload.clienteNombre) {
    txt('CLIENTE: '); textLn(payload.clienteNombre);
  }
  if (payload.localTelefono) {
    txt('TEL: '); textLn(payload.localTelefono);
  }
  push(CMD.BOLD_OFF);

  // --- DETALLE ---
  push(CMD.ALIGN_CENTER);
  dashes();
  push(CMD.BOLD_ON);
  textLn('DETALLE');
  push(CMD.BOLD_OFF);
  dashes();
  push(CMD.ALIGN_LEFT);

  // Build bloques using native formatter
  const bloques = _buildNativeBloques(payload.jugadas, _fmtMontoNative);

  let escGeneralHeaderDone = false;
  for (const bloque of bloques) {
    if (bloque.tipo === 'titulo') {
      push(CMD.BOLD_ON);
      textLn(bloque.texto);
      push(CMD.BOLD_OFF);
    } else if (bloque.tipo === 'monazos') {
      // Concept title only (no "MONTO NUMEROS" sub-header)
      push(CMD.BOLD_ON);
      textLn(bloque.titulo);
      push(CMD.BOLD_OFF);
      push(CMD.BOLD_ON);
      for (let i = 0; i < bloque.lineas.length; i++) {
        if (i === 0) {
          textLn(bloque.monto + ' ' + bloque.lineas[i]);
        } else {
          textLn('  ' + bloque.lineas[i]);
        }
      }
      push(CMD.BOLD_OFF);
    } else if (bloque.tipo === 'general') {
      if (!escGeneralHeaderDone) {
        push(CMD.BOLD_ON);
        textLn('MONTO    NUMEROS');
        push(CMD.BOLD_OFF);
        dashes();
        escGeneralHeaderDone = true;
      }
      push(CMD.BOLD_ON);
      for (let i = 0; i < bloque.lineas.length; i++) {
        if (i === 0) {
          textLn(bloque.monto + ' ' + bloque.lineas[i]);
        } else {
          textLn('       ' + bloque.lineas[i]);
        }
      }
      push(CMD.BOLD_OFF);
    } else if (bloque.tipo === 'reventado-tabla') {
      // 3-column table header (bold)
      push(CMD.BOLD_ON);
      textLn('NUMEROS       |DIR    |REV');
      push(CMD.BOLD_OFF);
      dashes();
      // Data rows: bold for legibility
      push(CMD.BOLD_ON);
      for (const fila of bloque.filas) {
        const nums = fila.numeros.padEnd(14).slice(0, 14);
        const dir = ('|' + (fila.normal || '').padStart(6)).slice(0, 7);
        const rev = ('|' + (fila.reventado || '').padStart(6)).slice(0, 7);
        textLn(`${nums}${dir}${rev}`);
      }
      push(CMD.BOLD_OFF);
    }
  }

  // --- TOTAL (bold + double W+H, centered) ---
  push(CMD.ALIGN_CENTER);
  dashes();
  push(CMD.BOLD_ON, CMD.SIZE_DOUBLE_WH);
  textLn('TOTAL: ' + _fmtMontoNative(payload.total));
  push(CMD.SIZE_NORMAL, CMD.BOLD_OFF, CMD.FONT_B);

  // --- PIE ---
  push(CMD.ALIGN_CENTER);
  if (payload.fechaOperativa) {
    push(CMD.BOLD_ON);
    textLn('VALIDO PARA EL DIA');
    textLn(payload.fechaOperativa);
    push(CMD.BOLD_OFF);
  }
  const leyenda = payload.leyendaLocal || 'GRACIAS POR SU PREFERENCIA';
  const wrappedLeyenda = _wrapText(leyenda, 32);
  for (const wl of wrappedLeyenda) {
    textLn(wl);
  }
  textLn('TIQUETE: ' + payload.ticketCode);
  push(CMD.ALIGN_LEFT);

  // Feed final (más espacio para que el pie no quede al borde)
  ln(); ln(); ln(); ln(); ln();

  return new Uint8Array(b);
}

/**
 * Genera comandos estructurados para impresión SUNMI SDK (sin ESC/POS raw).
 * Usa printText del SDK que renderiza con fuentes TrueType de Android,
 * eliminando el slash del 0 del character ROM del printer.
 * Números centrales más grandes (30px vs 24px).
 * MONAZOS: titulo una sola vez por concepto, sin sub-headers repetidos.
 * Formato: JSON array de [align(0|1|2), bold(0|1), sizePx, texto].
 */
export function ticketPayloadToSunmiCommands(payload: {
  ticketCode: string;
  sorteoNombre: string;
  fecha?: string;
  total: number;
  jugadas: Array<{ num1: string; num2?: string | null; monto: number; tipoSorteo?: string }>;
  localNombre?: string;
  localDescripcion?: string;
  sorteoCierre?: string;
  vendedorNombre?: string;
  clienteNombre?: string;
  pagaVecesAplicado?: string | null;
  esReimpresion?: boolean;
  leyendaLocal?: string;
  localTelefono?: string;
  fechaOperativa?: string;
}): string {
  type Cmd = [number, number, number, string];
  const cmds: Cmd[] = [];
  // Tamaños para APK SUNMI (ligeramente más grandes para legibilidad)
  const S = 24;
  const D = 26;   // Detail numbers — slightly larger than S for legibility
  const L = 28;
  const XL = 32;
  const SM = 22;
  const dash = '--------------------------------';

  // Debug: log phone to trace why it might not appear
  console.log('[SUNMI CMD] localTelefono:', JSON.stringify(payload.localTelefono), 'leyenda:', JSON.stringify(payload.leyendaLocal));

  if (payload.esReimpresion) {
    cmds.push([1, 1, XL, '** REIMPRESION **\n']);
  }

  // Encabezado
  cmds.push([1, 1, L, (payload.localNombre || 'TIEMPOS').toUpperCase() + '\n']);
  cmds.push([1, 0, SM, dash + '\n']);

  // Sorteo
  cmds.push([1, 1, XL, payload.sorteoNombre.toUpperCase() + '\n']);
  cmds.push([1, 0, SM, dash + '\n']);

  // Meta (más compacta)
  if (payload.fecha) cmds.push([0, 1, SM, 'VENTA: ' + payload.fecha + '\n']);
  if (payload.pagaVecesAplicado) cmds.push([0, 1, SM, 'Paga: ' + payload.pagaVecesAplicado + '\n']);
  if (payload.clienteNombre) cmds.push([0, 1, SM, 'CLIENTE: ' + payload.clienteNombre + '\n']);
  if (payload.localTelefono) cmds.push([0, 1, SM, 'TEL: ' + payload.localTelefono + '\n']);

  // Detalle (un solo separador)
  cmds.push([1, 0, SM, dash + '\n']);
  cmds.push([1, 1, S, 'DETALLE\n']);

  const bloques = _buildNativeBloques(payload.jugadas, _fmtMontoNative);
  let lastMonazosTitulo = '';
  let generalHeaderDone = false;

  for (const bloque of bloques) {
    if (bloque.tipo === 'titulo') {
      cmds.push([0, 1, S, bloque.texto + '\n']);
    } else if (bloque.tipo === 'monazos') {
      // Titulo solo una vez por concepto (sin sub-header repetido)
      if (bloque.titulo !== lastMonazosTitulo) {
        cmds.push([0, 1, S, bloque.titulo + '\n']);
        lastMonazosTitulo = bloque.titulo;
      }
      for (let i = 0; i < bloque.lineas.length; i++) {
        const pre = i === 0 ? bloque.monto + ' ' : '  ';
        cmds.push([0, 1, D, pre + bloque.lineas[i] + '\n']);
      }
    } else if (bloque.tipo === 'general') {
      if (!generalHeaderDone) {
        cmds.push([0, 1, S, 'MONTO  NUMEROS\n']);
        generalHeaderDone = true;
      }
      for (let i = 0; i < bloque.lineas.length; i++) {
        // Pad monto to fixed 6 chars so numbers always start at column 7
        const pre = i === 0 ? bloque.monto.padEnd(6) + ' ' : '       ';
        cmds.push([0, 1, D, pre + bloque.lineas[i] + '\n']);
      }
    } else if (bloque.tipo === 'reventado-tabla') {
      const h = 'NUMEROS   |  DIR|  REV';
      cmds.push([0, 1, S, h + '\n']);
      for (const fila of bloque.filas) {
        const nums = fila.numeros.padEnd(10).slice(0, 10);
        const dir = '|' + (fila.normal || '').padStart(5);
        const rev = '|' + (fila.reventado || '').padStart(5);
        cmds.push([0, 1, D, nums + dir + rev + '\n']);
      }
    }
  }

  // Total
  cmds.push([1, 0, SM, dash + '\n']);
  cmds.push([1, 1, XL, 'TOTAL: ' + _fmtMontoNative(payload.total) + '\n']);

  // Pie (compacto, con word-wrap para que no se salga de márgenes)
  if (payload.fechaOperativa) {
    cmds.push([1, 1, SM, 'VALIDO PARA EL DIA\n']);
    cmds.push([1, 1, SM, payload.fechaOperativa + '\n']);
  }
  const leyendaRaw = payload.leyendaLocal || 'GRACIAS POR SU PREFERENCIA';
  const MAX_LINE = 28; // ~28 chars fit at SM=22 on 384px paper
  const leyendaLines = _wrapText(leyendaRaw, MAX_LINE);
  for (const ll of leyendaLines) {
    cmds.push([1, 0, SM, ll + '\n']);
  }
  cmds.push([1, 0, SM, 'TIQUETE: ' + payload.ticketCode + '\n']);
  // Extra feed para que el pie no quede cortado por el borde de corte
  cmds.push([0, 0, SM, ' \n']);
  cmds.push([0, 0, SM, ' \n']);
  cmds.push([0, 0, SM, ' \n']);
  cmds.push([0, 0, SM, ' \n']);
  cmds.push([0, 0, SM, ' \n']);

  return JSON.stringify(cmds);
}

// ---------------------------------------------------------------------------
// Annulation receipt formatters
// ---------------------------------------------------------------------------

export interface AnulacionPayload {
  ticketCode: string;
  total: number;
  jugadas?: Array<{ num1: string; num2?: string | null; monto: number }>;
  localNombre?: string;
  fechaAnulacion: string;
  vendedorNombre?: string;
}

/**
 * Generates plain text for annulation receipt (ESC/POS or SUNMI fallback).
 */
export function anulacionToText(payload: AnulacionPayload): string {
  const lines: string[] = [];
  const hr = '================================';
  const COL = 32;
  const center = (s: string) => {
    const p = Math.max(0, Math.floor((COL - s.length) / 2));
    return ' '.repeat(p) + s;
  };

  lines.push(center('*** TICKET ANULADO ***'));
  lines.push('');
  const nombre = (payload.localNombre || 'TIEMPOS').toUpperCase();
  lines.push(center(nombre));
  lines.push(hr);
  lines.push('TICKET:  ' + payload.ticketCode);
  lines.push('FECHA:   ' + payload.fechaAnulacion);
  if (payload.vendedorNombre) {
    lines.push('VENDEDOR:' + payload.vendedorNombre);
  }
  lines.push(hr);

  // Show jugadas if available
  if (payload.jugadas && payload.jugadas.length > 0) {
    lines.push(center('JUGADAS ANULADAS'));
    lines.push(hr);
    for (const j of payload.jugadas) {
      const num = j.num2 ? `${j.num1}-${j.num2}` : j.num1;
      lines.push(`${num.padEnd(12)} ${_fmtMonto(j.monto).padStart(10)}`);
    }
    lines.push(hr);
  }

  lines.push('TOTAL ANULADO: ' + _fmtMonto(payload.total));
  lines.push(hr);
  lines.push('');
  lines.push(center('ANULACION CONFIRMADA'));
  lines.push('');

  return lines.join('\n');
}

/**
 * Generates ESC/POS bytes for annulation receipt with formatting.
 */
export function anulacionToEscPosBytes(payload: AnulacionPayload): Uint8Array {
  const enc = new TextEncoder();
  const b: number[] = [];

  const push = (...cmds: (readonly number[])[]) => {
    for (const cmd of cmds) b.push(...cmd);
  };
  const txt = (s: string) => b.push(...Array.from(enc.encode(s)));
  const ln = () => b.push(LF);
  const textLn = (s: string) => { txt(s); ln(); };
  const dashes = () => textLn('--------------------------------');

  push(CMD.INIT);
  push(CMD.FONT_B);
  b.push(ESC, 0x52, 0x00);

  // Header - ANULADO in bold, double size
  push(CMD.ALIGN_CENTER, CMD.BOLD_ON, CMD.SIZE_DOUBLE_WH);
  textLn('*** ANULADO ***');
  push(CMD.SIZE_NORMAL, CMD.BOLD_OFF, CMD.FONT_B);

  // Local name
  push(CMD.ALIGN_CENTER, CMD.BOLD_ON, CMD.SIZE_DOUBLE_H);
  textLn((payload.localNombre || 'TIEMPOS').toUpperCase());
  push(CMD.SIZE_NORMAL, CMD.BOLD_OFF, CMD.FONT_B);
  dashes();

  // Ticket info
  push(CMD.ALIGN_LEFT, CMD.BOLD_ON);
  txt('TICKET: '); textLn(payload.ticketCode);
  txt('FECHA:  '); textLn(payload.fechaAnulacion);
  if (payload.vendedorNombre) {
    txt('VENDEDOR: '); textLn(payload.vendedorNombre);
  }
  push(CMD.BOLD_OFF);
  dashes();

  // Jugadas if available
  if (payload.jugadas && payload.jugadas.length > 0) {
    push(CMD.ALIGN_CENTER, CMD.BOLD_ON);
    textLn('JUGADAS ANULADAS');
    push(CMD.BOLD_OFF);
    dashes();
    push(CMD.ALIGN_LEFT);

    for (const j of payload.jugadas) {
      const num = j.num2 ? `${j.num1}-${j.num2}` : j.num1;
      textLn(`${num.padEnd(12)} ${_fmtMontoNative(j.monto).padStart(10)}`);
    }
    dashes();
  }

  // Total
  push(CMD.ALIGN_CENTER, CMD.BOLD_ON, CMD.SIZE_DOUBLE_WH);
  textLn('TOTAL: ' + _fmtMontoNative(payload.total));
  push(CMD.SIZE_NORMAL, CMD.BOLD_OFF, CMD.FONT_B);
  dashes();

  // Footer
  push(CMD.ALIGN_CENTER);
  textLn('ANULACION CONFIRMADA');

  ln(); ln(); ln(); ln(); ln();

  return new Uint8Array(b);
}

/**
 * Generates SUNMI SDK commands for annulation receipt.
 */
export function anulacionToSunmiCommands(payload: AnulacionPayload): string {
  type Cmd = [number, number, number, string];
  const cmds: Cmd[] = [];
  const S = 24;
  const L = 28;
  const XL = 32;
  const SM = 22;
  const dash = '--------------------------------';

  // Header - ANULADO
  cmds.push([1, 1, XL, '*** ANULADO ***\n']);
  cmds.push([1, 1, L, (payload.localNombre || 'TIEMPOS').toUpperCase() + '\n']);
  cmds.push([1, 0, SM, dash + '\n']);

  // Ticket info
  cmds.push([0, 1, SM, 'TICKET: ' + payload.ticketCode + '\n']);
  cmds.push([0, 1, SM, 'FECHA:  ' + payload.fechaAnulacion + '\n']);
  if (payload.vendedorNombre) {
    cmds.push([0, 1, SM, 'VENDEDOR: ' + payload.vendedorNombre + '\n']);
  }
  cmds.push([1, 0, SM, dash + '\n']);

  // Jugadas if available
  if (payload.jugadas && payload.jugadas.length > 0) {
    cmds.push([1, 1, S, 'JUGADAS ANULADAS\n']);
    cmds.push([1, 0, SM, dash + '\n']);

    for (const j of payload.jugadas) {
      const num = j.num2 ? `${j.num1}-${j.num2}` : j.num1;
      cmds.push([0, 1, S, `${num.padEnd(12)} ${_fmtMontoNative(j.monto).padStart(10)}\n`]);
    }
    cmds.push([1, 0, SM, dash + '\n']);
  }

  // Total
  cmds.push([1, 1, XL, 'TOTAL: ' + _fmtMontoNative(payload.total) + '\n']);
  cmds.push([1, 0, SM, dash + '\n']);

  // Footer
  cmds.push([1, 1, SM, 'ANULACION CONFIRMADA\n']);
  cmds.push([0, 0, SM, ' \n']);
  cmds.push([0, 0, SM, ' \n']);
  cmds.push([0, 0, SM, ' \n']);
  cmds.push([0, 0, SM, ' \n']);
  cmds.push([0, 0, SM, ' \n']);

  return JSON.stringify(cmds);
}

/**
 * Convierte un payload de resumen de premios a texto plano para SUNMI.
 * Usado por LocalGanadoresPage.
 */
export function resumenPremiosToText(payload: {
  local: { nombre: string };
  sorteo: string;
  fecha: string;
  ganador: string;
  tickets: Array<{ ticket: string; monto: number; premio: number }>;
  totalPagado: number;
  totalRecaudado: number;
  sorteoCierre?: string | null;
}): string {
  const lines: string[] = [];
  const hr = '================================';
  const col = 32;

  lines.push('');
  lines.push(payload.local.nombre.toUpperCase().slice(0, col).padStart(Math.floor((col + payload.local.nombre.length) / 2)));
  lines.push('');
  lines.push(hr);
  lines.push('RESUMEN DE PREMIOS');
  lines.push(hr);
  lines.push('');
  lines.push(`Sorteo: ${payload.sorteo}`);
  if (payload.sorteoCierre) {
    lines.push(`Cierre: ${payload.sorteoCierre}`);
  }
  lines.push(`Fecha: ${payload.fecha}`);
  lines.push('');
  lines.push(hr);
  lines.push(`NUMERO GANADOR: ${payload.ganador}`);
  lines.push(hr);
  lines.push('');

  if (payload.tickets.length > 0) {
    lines.push('TICKET       MONTO     PREMIO');
    lines.push('--------------------------------');
    for (const t of payload.tickets) {
      const ticket = t.ticket.padEnd(12).slice(0, 12);
      const monto = `C${Math.trunc(t.monto)}`.padStart(8);
      const premio = `C${Math.trunc(t.premio)}`.padStart(10);
      lines.push(`${ticket}${monto}${premio}`);
    }
    lines.push('');
  } else {
    lines.push('No hay tickets ganadores');
    lines.push('');
  }

  lines.push(hr);
  lines.push(`RECAUDADO: ${_fmtMonto(payload.totalRecaudado)}`);
  lines.push(`PAGADO:    ${_fmtMonto(payload.totalPagado)}`);
  lines.push(hr);
  lines.push('');
  lines.push('\n\n');

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Comprobante de pago de premio
// ---------------------------------------------------------------------------

export interface ComprobantePremioPayload {
  ticketNumber: string;
  sorteoNombre: string;
  monto: number;
  pagadoPor: string;
  fechaPago: string; // ISO string — se formatea dentro
}

export function comprobantePremioToText(p: ComprobantePremioPayload): string {
  const COL = 32;
  const hr = '================================';
  const center = (s: string) => {
    const pad = Math.max(0, Math.floor((COL - s.length) / 2));
    return ' '.repeat(pad) + s;
  };
  const row = (label: string, value: string) => {
    const space = COL - label.length - value.length;
    return label + ' '.repeat(Math.max(1, space)) + value;
  };

  const dt = parseBackendDate(p.fechaPago);
  const fecha = formatFechaCR(dt);
  const hora = formatHoraCR(dt);

  return [
    hr,
    center('COMPROBANTE DE PREMIO'),
    hr,
    row('Tiquete:', p.ticketNumber),
    row('Sorteo: ', p.sorteoNombre),
    hr.replace(/=/g, '-'),
    row('MONTO:  ', `C.${p.monto.toLocaleString('es-CR')}`),
    hr.replace(/=/g, '-'),
    row('Pagado por:', p.pagadoPor),
    row('Fecha:     ', fecha),
    row('Hora:      ', hora),
    hr,
    '',
  ].join('\n');
}

